import type { OapRow } from '@/logic/oapAttainment';
import { formatReportDate } from '@/logic/cmsWindows';
import MeasureTile from './MeasureTile';
import Sparkline from './Sparkline';
import GoalStatusTag from './GoalStatusTag';

// D1: the CMS goal is what the row prints and the care plan's guideline target rides the goal
// cell's tooltip only — one row must never show two targets and one verdict.
export default function GoalRow({ row, loading = false, seriesError = false }: { row: OapRow; loading?: boolean; seriesError?: boolean }) {
  const deviceRow = row.key === 'bp' || row.key === 'weight';
  const pending = loading && deviceRow;
  const failed = seriesError && deviceRow && !pending;
  const goalTip = row.goal
    ? `${row.goal.detail}${row.goal.planTarget ? ` Care plan target: ${row.goal.planTarget}.` : ''}`
    : undefined;
  return (
    <div className="oap-row" role="row" data-measure={row.key}>
      <div className="oap-cell oap-cell--measure" role="cell">
        <MeasureTile category={row.category} />
        <span className="oap-measure">
          <span className="body2 oap-measure__label">{row.label}</span>
          <span className="detail1 text-secondary">
            {row.unit}
            {row.required ? '' : ' · not required'}
          </span>
        </span>
      </div>
      <div className="oap-cell" role="cell">
        {row.baseline
          ? <span className="body2">{row.baseline.display}</span>
          : <span className="detail1 text-secondary">{row.baselineNote}</span>}
      </div>
      <div className="oap-cell" role="cell">
        {pending && <span className="skeleton-line" style={{ width: 56 }} />}
        {failed && <span className="detail1 text-secondary">readings unavailable</span>}
        {!pending && !failed && row.current && (
          <>
            <span className="body2">{row.current.display}</span>
            <span className="detail1 text-secondary">
              {row.current.at != null ? formatReportDate(row.current.at * 1000) : row.current.note ?? ''}
            </span>
          </>
        )}
        {!pending && !failed && !row.current && <span className="body2 text-secondary">—</span>}
      </div>
      <div className="oap-cell oap-cell--spark text-secondary" role="cell">
        {!pending && !failed && row.series && <Sparkline points={row.series} />}
      </div>
      <div className="oap-cell" role="cell">
        {row.goal ? (
          <span className="body2 wi-tip oap-goal" data-tip={goalTip} role="note" aria-label={`${row.goal.display}. ${goalTip}`}>{row.goal.display}</span>
        ) : <span className="detail1 text-secondary">no target</span>}
      </div>
      <div className="oap-cell" role="cell">
        {!pending && !failed && row.gap && <span className="body2">{row.gap}</span>}
      </div>
      <div className="oap-cell" role="cell">
        {pending && <span className="skeleton-line" style={{ width: 72 }} />}
        {failed && <span className="oap-pill" style={{ background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)' }}>Readings unavailable</span>}
        {!pending && !failed && <GoalStatusTag status={row.status} label={row.statusLabel} />}
      </div>
    </div>
  );
}
